/**
 * parentMessaging.ts
 *
 * Fork-side helpers for the DL wrapper embed performance PRD (§6.5.4).
 *
 * When the fork is loaded inside the DL wrapper iframe (embedded build,
 * `window.parent !== window`), these helpers post tightly-scoped messages
 * back to the wrapper so the wrapper can drive the overlay lifecycle
 * without polling the fork.
 *
 * Notes:
 * - `notifyParentReady()` is fire-once per iframe lifetime via a
 *   module-level flag (`hasFiredReady`). State flicker in the calling
 *   effect will not re-post.
 * - `notifyParentSessionExpired()` has NO fire-once guard — multiple
 *   401 signals during a session can all fire. The wrapper's retry
 *   guardrails (debounce / budget) handle the volume.
 * - Origin is resolved once via the cached
 *   `LightdashUiEvent.getTargetOriginFromUrl()` helper and reused for
 *   subsequent calls. If no `targetOrigin` search param is present, or
 *   the value fails URL parsing, both helpers become no-ops (we never
 *   post to `'*'`).
 * - We intentionally bypass `LightdashUiEvent.dispatch()` because that
 *   helper requires `pathname.startsWith('/embed/')`, which our
 *   wrapper-embedded top-level route does NOT use.
 */
import { LightdashUiEvent } from './events/LightdashUiEvent';

/**
 * Embedded build detection. Vite's `--mode embedded` sets
 * `import.meta.env.MODE === 'embedded'`. The standalone build keeps
 * the same file on disk — every intercept that imports from here is
 * double-gated with this constant so standalone deployments behave
 * identically to the pre-PRD fork.
 */
export const IS_EMBEDDED_BUILD: boolean =
    import.meta.env.MODE === 'embedded';

/**
 * Resolve `targetOrigin` SYNCHRONOUSLY at module-import time. The
 * Lightdash client-side router mounts after the bundle evaluates and
 * can rewrite the iframe URL (e.g. redirecting `/` → `/projects/:uuid`
 * via `<Navigate>`), which strips the `?targetOrigin=...` search param
 * before any `useEffect` fires. Resolving lazily (on first function
 * call) is therefore unreliable — by the time PrivateRoute's
 * `isFullyReady` effect runs, `window.location.search` has already
 * been replaced and `getTargetOriginFromUrl()` returns undefined.
 *
 * Capturing at module load (which happens during initial JS bundle
 * parse, before any React render) guarantees we see the original URL.
 */
let INITIAL_TARGET_ORIGIN: string | null = (() => {
    try {
        return LightdashUiEvent.getTargetOriginFromUrl() ?? null;
    } catch {
        return null;
    }
})();

function getParentOrigin(): string | undefined {
    return INITIAL_TARGET_ORIGIN ?? undefined;
}

/**
 * DL Fork: expose the module-load-captured
 * `targetOrigin` so the BI bridge can re-validate it against the health
 * endpoint's `embedding.postMessage` allowlist before activating the
 * wrapper<->iframe channel. Returns `null` when no (parseable) `targetOrigin`
 * URL param was present at bundle load. Callers MUST NOT trust this value on
 * its own — it is the RAW captured param and must still pass
 * `isAllowedPostMessageOrigin` against the server-served allowlist.
 */
export function getCapturedTargetOrigin(): string | null {
    return INITIAL_TARGET_ORIGIN;
}

/**
 * Module-level fire-once guard for `notifyParentReady`. Reset only by
 * a full iframe remount (the wrapper re-loads the fork bundle),
 * which is what happens on `sessionEpoch++` in the wrapper.
 */
let hasFiredReady = false;

/**
 * Emit `lightdash:ready` to the parent wrapper. Fires at most once
 * per iframe lifetime. No-op when:
 * - not in an iframe (`window.parent === window`)
 * - no `targetOrigin` URL param (or it's malformed)
 * - `hasFiredReady` already latched
 * - `postMessage` throws (cross-origin anomalies, wrapper destroyed)
 */
export function notifyParentReady(): void {
    if (hasFiredReady) return;
    if (window.parent === window) return;

    const origin = getParentOrigin();
    if (!origin) return;

    try {
        window.parent.postMessage(
            { type: 'lightdash:ready', timestamp: Date.now() },
            origin,
        );
        hasFiredReady = true;
    } catch {
        // Swallow: wrapper may have been torn down, or browser threw
        // for an esoteric cross-origin reason. Any failure here is
        // non-actionable from inside the iframe.
    }
}

/**
 * Emit `lightdash:session-expired` to the parent wrapper. NO
 * fire-once guard — intentional, so multiple signals from
 * PrivateRoute / useQueryError / api.ts / visibility probe can all
 * reach the wrapper. Wrapper-side debounce + 60 s retry budget
 * collapses bursts into a single remount.
 *
 * No-op when:
 * - not in an iframe (`window.parent === window`)
 * - no `targetOrigin` URL param (or it's malformed)
 * - `postMessage` throws
 */
export function notifyParentSessionExpired(): void {
    if (window.parent === window) return;

    const origin = getParentOrigin();
    if (!origin) return;

    try {
        window.parent.postMessage(
            { type: 'lightdash:session-expired', timestamp: Date.now() },
            origin,
        );
    } catch {
        // Swallow — see notifyParentReady.
    }
}

/**
 * TEST ONLY: reset internal state so vitest `describe` blocks can
 * assert fire-once behaviour across multiple cases without
 * re-importing the module. Not exported via a barrel — consumers
 * must import the file directly. Do NOT call from production code.
 */
export function __resetParentMessagingForTests(): void {
    hasFiredReady = false;
    // DL Fork: re-capture so tests can vary window.location between cases
    INITIAL_TARGET_ORIGIN = LightdashUiEvent.getTargetOriginFromUrl() ?? null;
}
